import { cashRegisterMovementApi } from "./axios";
import { useQuery } from "@tanstack/react-query";
import { ValidateCurrentToken, LogoutSystem } from "../../helpers";
import {
  ServiceResponseI,
  DataI,
  GetCashRegisterMovementParamsI,
  ListCashRegisterMovementI,
  OneCashRegisterMovementForFrontI,
  CreateMovementI,
  TransferCashRegisterI,
} from "../../interfaces";
import { AxiosError } from "axios";

const getCashRegisterMovements = async ({
  pagination,
  sorting,
  globalFilter,
  branchId,
  cashRegisterId,
  dateFrom,
  dateTo,
}: GetCashRegisterMovementParamsI): Promise<
  ServiceResponseI<DataI<ListCashRegisterMovementI>>
> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const params = new URLSearchParams();
    params.append("page", (pagination.pageIndex + 1).toString());
    params.append("limit", pagination.pageSize.toString());
    params.append("search", globalFilter ?? "");
    params.append("sorting", JSON.stringify(sorting ?? []));
    if (branchId) params.append("branchId", branchId);
    if (cashRegisterId) params.append("cashRegisterId", cashRegisterId);
    if (dateFrom) params.append("dateFrom", dateFrom);
    if (dateTo) params.append("dateTo", dateTo);

    const { data } = await cashRegisterMovementApi.get<
      ServiceResponseI<DataI<ListCashRegisterMovementI>>
    >("", {
      params,
      headers: { Authorization: validateToken },
    });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const cashRegisterMovementById = async (
  id: string,
): Promise<ServiceResponseI<OneCashRegisterMovementForFrontI>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await cashRegisterMovementApi.get<
      ServiceResponseI<OneCashRegisterMovementForFrontI>
    >(`/${id}`, { headers: { Authorization: validateToken } });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const cashRegisterMovementPost = async (
  payload: CreateMovementI,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await cashRegisterMovementApi.post<
      ServiceResponseI<string>
    >("", payload, { headers: { Authorization: validateToken } });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const cashRegisterMovementAnnul = async (
  id: string,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await cashRegisterMovementApi.delete<
      ServiceResponseI<string>
    >(`/${id}`, { headers: { Authorization: validateToken } });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const cashRegisterMovementTransfer = async (
  payload: TransferCashRegisterI,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await cashRegisterMovementApi.post<
      ServiceResponseI<string>
    >("/transfer", payload, { headers: { Authorization: validateToken } });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const useCashRegisterMovements = ({
  pagination,
  sorting,
  globalFilter,
  branchId,
  cashRegisterId,
  dateFrom,
  dateTo,
}: GetCashRegisterMovementParamsI) => {
  const cashRegisterMovementsQuery = useQuery(
    [
      "cash-register-movements",
      {
        pagination,
        sorting,
        globalFilter,
        branchId,
        cashRegisterId,
        dateFrom,
        dateTo,
      },
    ],
    () =>
      getCashRegisterMovements({
        pagination,
        sorting,
        globalFilter,
        branchId,
        cashRegisterId,
        dateFrom,
        dateTo,
      }),
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    },
  );

  return { cashRegisterMovementsQuery };
};
